import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCrown, 
  faSnowflake,
  faCalendarAlt,
  faArrowRight,
  faInfoCircle,
  faClock,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { isCastleOpenAllDaysThisWeek } from '../../../utils/openingHoursUtils';

// Import images
import castleImage from '../../../assets/castle2.png';
import christmasImage from '../../../assets/A-Magical-Night-Before-Christmas.png';

const BookingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  margin-top: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 2px solid transparent;
`;

const OptionImage = styled.div`
  height: 300px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '${props => props.badge}';
    position: absolute;
    top: 15px;
    right: 15px;
    background: ${props => props.theme.colors.accent};
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
  }
`;

const OptionContent = styled.div`
  padding: 2rem;
`;

const OptionTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const OptionDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2rem;
`;

const OptionDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const BookButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.theme.colors.accent};
  font-size: 1.1rem;
  cursor: pointer;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.greyBackground}, #f8f8f8);
  padding: 2rem;
  border-radius: 12px;
  margin-top: 4rem;
  text-align: center;
  border: 1px solid ${props => props.theme.colors.accent};
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const DateNotice = styled.div`
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
  border: 2px solid #fdcb6e;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
  
  h3 {
    color: #d63031;
    font-size: 1.3rem;
    margin-bottom: 1rem;
    font-weight: 600;
  }
  
  p {
    color: #2d3436;
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

const BookingSelection = () => {
  const navigate = useNavigate();
  
  // Check if we're in Christmas season (November onwards)
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11 (January = 0)
  const currentDay = currentDate.getDate();
  
  // Christmas season: November 29th onwards
  const isChristmasSeason = currentMonth >= 10 && currentDay >= 29;
  
  // Christmas tickets are available for booking now (even before the season starts)
  const isChristmasBookingAvailable = true;
  
  // Function to handle booking selection
  const handleBooking = (bookingType) => {
    // All bookings now go to the new booking journey
    window.location.href = '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';
  };

  return (
    <BookingContainer>
      <ParallaxBanner 
        title="Book Your Castle Visit"
        subtitle="Choose Your Perfect Experience"
        image={isChristmasSeason ? christmasImage : castleImage}
      />
      
      <ContentContainer>
        <SectionTitle>Select Your Booking Type</SectionTitle>
        <IntroText>
          Choose the perfect way to experience Belvoir Castle. Select from our available booking options below.
        </IntroText>
        
        {isChristmasSeason && (
          <DateNotice>
            <h3>🎄 Christmas Season Now Available!</h3>
            <p>
              Experience Belvoir Castle during our festive season starting November 29th. 
              Book your Christmas visit and enjoy the castle during this special time of year.
            </p>
          </DateNotice>
        )}
        
                <OptionsGrid>
          {isChristmasSeason ? (
            // Christmas season - show Christmas booking as primary
            <>
              <OptionCard>
                <OptionImage image={christmasImage} badge="CHRISTMAS" />
                <OptionContent>
                  <OptionTitle>
                    <FontAwesomeIcon icon={faSnowflake} />
                    Christmas at Belvoir Castle
                  </OptionTitle>
                  <OptionDescription>
                    Experience Belvoir Castle during the festive season with our Christmas tickets. 
                    Visit the castle and gardens during this special time of year and enjoy the 
                    historic atmosphere of a castle during the Christmas period.
                  </OptionDescription>
                  <OptionDetails>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <div>
                        <strong>Available:</strong><br />
                         November 29th onwards
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faClock} />
                      <div>
                        <strong>Times:</strong><br />
                        10:00 - 17:00
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faUsers} />
                      <div>
                        <strong>Includes:</strong><br />
                        Castle & Gardens
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCrown} />
                      <div>
                        <strong>Season:</strong><br />
                        Christmas period
                      </div>
                    </DetailItem>
                  </OptionDetails>
                  <BookButton onClick={() => handleBooking('christmas')}>
                    Book Christmas Visit <FontAwesomeIcon icon={faArrowRight} />
                  </BookButton>
                </OptionContent>
              </OptionCard>

              <OptionCard>
                <OptionImage image={castleImage} badge="GENERAL" />
                <OptionContent>
                  <OptionTitle>
                    <FontAwesomeIcon icon={faCrown} />
                    General Castle Visit
                  </OptionTitle>
                  <OptionDescription>
                    Book your standard castle visit with access to the State Rooms, Gardens, and all 
                    regular attractions. Perfect for experiencing the castle's rich history and beautiful grounds.
                  </OptionDescription>
                  <OptionDetails>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <div>
                        <strong>Available:</strong><br />
                        Until end of October
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faClock} />
                      <div>
                        <strong>Times:</strong><br />
                        10:00 - 16:00
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faUsers} />
                      <div>
                        <strong>Includes:</strong><br />
                        Castle & Gardens
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCrown} />
                      <div>
                        <strong>Standard:</strong><br />
                        Regular admission
                      </div>
                    </DetailItem>
                  </OptionDetails>
                  <BookButton onClick={() => handleBooking('general')}>
                    Book General Visit <FontAwesomeIcon icon={faArrowRight} />
                  </BookButton>
                </OptionContent>
              </OptionCard>
            </>
          ) : (
            // General season - show general booking as primary, but Christmas is available for booking
            <>
              <OptionCard>
                <OptionImage image={castleImage} badge="GENERAL" />
                <OptionContent>
                  <OptionTitle>
                    <FontAwesomeIcon icon={faCrown} />
                    General Castle Visit
                  </OptionTitle>
                  <OptionDescription>
                    Book your castle visit with access to the State Rooms, Gardens, and all regular attractions. 
                    Experience the rich history and beautiful grounds of Belvoir Castle.
                  </OptionDescription>
                  <OptionDetails>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <div>
                        <strong>Available:</strong><br />
                        Until end of October
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faClock} />
                      <div>
                        <strong>Times:</strong><br />
                        10:00 - 17:00
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faUsers} />
                      <div>
                        <strong>Includes:</strong><br />
                        Castle & Gardens
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCrown} />
                      <div>
                        <strong>Standard:</strong><br />
                        Regular admission
                      </div>
                    </DetailItem>
                  </OptionDetails>
                  <BookButton onClick={() => handleBooking('general')}>
                    Book Castle Visit <FontAwesomeIcon icon={faArrowRight} />
                  </BookButton>
                </OptionContent>
              </OptionCard>

              <OptionCard>
                <OptionImage image={christmasImage} badge="CHRISTMAS 2026" />
                <OptionContent>
                  <OptionTitle>
                    <FontAwesomeIcon icon={faSnowflake} />
                    Christmas at Belvoir Castle
                  </OptionTitle>
                  <OptionDescription>
                     Christmas bookings are now available! Experience Belvoir Castle during the 
                     festive season starting November 29th.
                  </OptionDescription>
                  <OptionDetails>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <div>
                        <strong>Available:</strong><br />
                         November 29th onwards
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faClock} />
                      <div>
                        <strong>Times:</strong><br />
                        10:00 - 17:00
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faUsers} />
                      <div>
                        <strong>Includes:</strong><br />
                        Castle & Gardens
                      </div>
                    </DetailItem>
                    <DetailItem>
                      <FontAwesomeIcon icon={faCrown} />
                      <div>
                        <strong>Season:</strong><br />
                        Christmas period
                      </div>
                    </DetailItem>
                  </OptionDetails>
                  <BookButton onClick={() => handleBooking('christmas')}>
                    Book Christmas Visit <FontAwesomeIcon icon={faArrowRight} />
                  </BookButton>
                </OptionContent>
              </OptionCard>
            </>
          )}
        </OptionsGrid>

        <InfoBox>
          <InfoText>
            <FontAwesomeIcon icon={faInfoCircle} /> 
           
          </InfoText>
          <InfoText>
            <strong>Opening Times:</strong> Castle and Gardens are open from 10:00 - 17:00 (last entry 16:00).{' '}
            {isCastleOpenAllDaysThisWeek()
              ? 'Castle is open every day this week.'
              : 'Castle closed Tuesday and Wednesday.'}
          </InfoText>
          <InfoText>
            <strong>Need help?</strong> Contact our reception team for assistance with your reservation on +44 (0)1476 871001.
          </InfoText>
        </InfoBox>
      </ContentContainer>
      
      <Footer />
    </BookingContainer>
  );
};

export default BookingSelection; 